import React, { createRef, type MouseEvent, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { YatSprite } from '@pkg/components/YatSprite';

import { type SidebarGroup } from '@app/helpers';

import './GroupThumb.scss';

interface YatSidebarGroupThumbProps {
    group: SidebarGroup;
    isActive: boolean;
    onClick: (e: MouseEvent<HTMLElement>) => void;
}

const YatSidebarGroupThumb = ({ group, isActive, onClick }: YatSidebarGroupThumbProps) => {
    const { t } = useTranslation(['dashboard']);
    const [maxDisplay, setMaxDisplay] = useState<number>(4);
    const ellipsisRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (group) setMaxDisplay(group.length > 4 ? 3 : 4);
    }, [group]);

    const classNames = useMemo(() => {
        const classNames: string[] = ['yat-sidebar-group-thumb'];
        if (isActive) classNames.push('yat-sidebar-group-thumb--has-active-item');
        return classNames.join(' ');
    }, [isActive]);

    const nodeRef = useRef(null);

    return (
        <div role="button" tabIndex={0} className={classNames} onClick={onClick}>
            <div>{group.name && <span className="yat-sidebar-group-thumb__title v2-text-xs">{group.name}</span>}</div>
            <span className="yat-sidebar-group-thumb__sr-title">
                {t('open_group_of_yats', { number: group.length })}
            </span>
            <div className="yat-sidebar-group-thumb__inner" aria-hidden="false">
                <TransitionGroup component={null} nodeRef={nodeRef}>
                    {group.firstChars
                        .filter((_, i) => i < maxDisplay)
                        .map((yat, i) => {
                            const itemRef = createRef<HTMLSpanElement>();
                            return (
                                <CSSTransition
                                    nodeRef={itemRef}
                                    key={i}
                                    timeout={2500}
                                    exit={false}
                                    classNames="fade-base"
                                    unmountOnExit
                                >
                                    <span ref={itemRef} className="yat-sidebar-group-thumb__item">
                                        <YatSprite emojiID={yat} />
                                    </span>
                                </CSSTransition>
                            );
                        })}
                </TransitionGroup>
                <CSSTransition
                    nodeRef={ellipsisRef}
                    in={maxDisplay === 3}
                    timeout={2500}
                    classNames="fade-base"
                    unmountOnExit
                >
                    <span
                        ref={ellipsisRef}
                        key="ellipsis"
                        className="yat-sidebar-group-thumb__item yat-sidebar-group-thumb__item--ellipsis-number"
                    >
                        +{group.length - 3 + ''}
                    </span>
                </CSSTransition>
            </div>
        </div>
    );
};

export default YatSidebarGroupThumb;
